import { useState, useMemo, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff,
  FileText,
  Activity,
  CheckCircle,
  AlertTriangle,
  XCircle,
  MessageSquare,
  Star,
} from 'lucide-react';
import { clsx } from 'clsx';
import { api } from '../../lib/api';
import type { Phase1Metrics } from './components/ConsistencyMetrics';
import { IterationComparisonCard, type Execution } from './components/IterationComparisonCard';
import { LLMConsistencyAnalysis } from './components/LLMConsistencyAnalysis';

interface TestRun {
  id: string;
  name: string;
  status: string;
  isRevealed: boolean;
  runType: 'MODEL_COMPARISON' | 'MODEL_STABILITY';
  testCase: {
    id: string;
    name: string;
    code: string;
    condition: string;
    patientInfo: {
      age: number;
      gender: string;
      chiefComplaint?: string;
    };
    answers: Array<{ question: string; answer: string }>;
  };
}

// Stability verdict options
const STABILITY_VERDICTS = [
  {
    value: 'HIGHLY_STABLE',
    label: 'Highly Stable',
    description: 'Consistent outputs across all iterations',
    icon: CheckCircle,
    color: 'text-green-600 bg-green-100 border-green-200',
    score: 100,
  },
  {
    value: 'STABLE',
    label: 'Stable',
    description: 'Minor variations, no clinical impact',
    icon: CheckCircle,
    color: 'text-blue-600 bg-blue-100 border-blue-200',
    score: 80,
  },
  {
    value: 'MODERATE',
    label: 'Moderate',
    description: 'Noticeable variations, acceptable range',
    icon: AlertTriangle,
    color: 'text-amber-600 bg-amber-100 border-amber-200',
    score: 60,
  },
  {
    value: 'UNSTABLE',
    label: 'Unstable',
    description: 'Significant variations, may affect decisions',
    icon: AlertTriangle,
    color: 'text-orange-600 bg-orange-100 border-orange-200',
    score: 40,
  },
  {
    value: 'HIGHLY_UNSTABLE',
    label: 'Highly Unstable',
    description: 'Unreliable outputs, major inconsistencies',
    icon: XCircle,
    color: 'text-red-600 bg-red-100 border-red-200',
    score: 20,
  },
];

// Calculate Phase 1 consistency metrics from executions
function calculatePhase1Metrics(executions: Execution[]): Phase1Metrics {
  // Suitability
  const suitabilityValues = executions.map((e) => e.indication?.suitability || 'N/A');
  const suitabilityCounts = suitabilityValues.reduce((acc, val) => {
    acc[val] = (acc[val] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);
  const maxSuitabilityCount = Math.max(...Object.values(suitabilityCounts));
  const suitabilityAgreement = (maxSuitabilityCount / executions.length) * 100;

  // Confidence
  const confidenceValues = executions.map((e) => e.indication?.confidence || 0);
  const confidenceMin = Math.min(...confidenceValues);
  const confidenceMax = Math.max(...confidenceValues);
  const confidenceMean = confidenceValues.reduce((a, b) => a + b, 0) / confidenceValues.length;
  const confidenceVariance = Math.sqrt(
    confidenceValues.reduce((sum, val) => sum + Math.pow(val - confidenceMean, 2), 0) / confidenceValues.length
  );

  // Issues - calculate Jaccard similarity
  const allIssues = executions.map((e) =>
    new Set((e.indication?.issues || []).map((i) => i.description.toLowerCase().trim()))
  );
  let issueOverlap = 100;
  if (allIssues.length > 1 && allIssues.some((s) => s.size > 0)) {
    const intersection = allIssues.reduce((acc, set) => {
      return new Set([...acc].filter((x) => set.has(x)));
    });
    const union = allIssues.reduce((acc, set) => {
      return new Set([...acc, ...set]);
    });
    issueOverlap = union.size > 0 ? (intersection.size / union.size) * 100 : 100;
  }

  // Criteria consistency
  const allCriteria = executions.map((e) =>
    (e.criteria?.criteria || []).map((c) => ({
      field: c.field || c.name || c.id,
      value: Array.isArray(c.value) ? c.value.join(',') : c.value,
    }))
  );

  // Get all unique criteria fields
  const allFields = new Set<string>();
  allCriteria.forEach((criteria) => {
    criteria.forEach((c) => allFields.add(c.field));
  });

  // Calculate how many fields appear in all executions
  let fieldsInAll = 0;
  let fieldsWithSameValue = 0;
  allFields.forEach((field) => {
    const valuesForField = allCriteria.map((criteria) => {
      const found = criteria.find((c) => c.field === field);
      return found?.value || null;
    });

    const presentInAll = valuesForField.every((v) => v !== null);
    if (presentInAll) {
      fieldsInAll++;
      const allSame = valuesForField.every((v) => v === valuesForField[0]);
      if (allSame) {
        fieldsWithSameValue++;
      }
    }
  });

  const criteriaConsistency = allFields.size > 0 ? (fieldsInAll / allFields.size) * 100 : 100;
  const criteriaValueMatch = fieldsInAll > 0 ? (fieldsWithSameValue / fieldsInAll) * 100 : 100;

  return {
    suitabilityAgreement,
    suitabilityValues,
    confidenceVariance,
    confidenceRange: { min: confidenceMin, max: confidenceMax },
    confidenceValues,
    issueOverlap,
    criteriaConsistency,
    criteriaValueMatch,
    totalIterations: executions.length,
  };
}

export function StabilityBlindEvaluation() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Form state
  const [verdict, setVerdict] = useState<string | null>(null);
  const [verdictReasoning, setVerdictReasoning] = useState('');
  const [observations, setObservations] = useState('');
  const [keyDifferences, setKeyDifferences] = useState<string[]>([]);
  const [newDifference, setNewDifference] = useState('');
  const [confidence, setConfidence] = useState(3);

  // Fetch blind view data
  const { data: blindData, isLoading } = useQuery({
    queryKey: ['validation', 'blind-view', id],
    queryFn: async () => {
      const response = await api.get<{
        testRun: TestRun;
        testCase: TestRun['testCase'];
        executions: Execution[];
      }>(`/api/validation/test-runs/${id}/blind-view`);
      return response;
    },
    enabled: !!id,
  });

  // Fetch existing stability evaluation (to pre-populate form)
  const { data: existingEvalData } = useQuery({
    queryKey: ['validation', 'stability-evaluation', id],
    queryFn: async () => {
      const response = await api.get<{
        testRunId: string;
        currentUser: {
          phase1Completed: boolean;
          phase2Completed: boolean;
          phase1Evaluation: {
            id: string;
            verdict: string;
            verdictReasoning?: string;
            observations?: string;
            keyDifferences?: string[];
            evaluatorConfidence?: number;
            consistencyMetrics?: Phase1Metrics;
          } | null;
          phase2Evaluation: unknown;
        };
      }>(`/api/validation/test-runs/${id}/stability-evaluation`);
      return response;
    },
    enabled: !!id,
  });

  // Pre-populate form with existing evaluation data
  useEffect(() => {
    const existing = existingEvalData?.currentUser?.phase1Evaluation;
    if (existing) {
      setVerdict(existing.verdict);
      setVerdictReasoning(existing.verdictReasoning || '');
      setObservations(existing.observations || '');
      setKeyDifferences(existing.keyDifferences || []);
      setConfidence(existing.evaluatorConfidence || 3);
    }
  }, [existingEvalData]);

  // Submit mutation
  const submitMutation = useMutation({
    mutationFn: async (data: {
      verdict: string;
      verdictReasoning?: string;
      observations?: string;
      keyDifferences: string[];
      evaluatorConfidence: number;
      consistencyMetrics: Phase1Metrics;
    }) => {
      const response = await api.post(`/api/validation/test-runs/${id}/stability-evaluation/phase1`, data);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['validation', 'test-run', id] });
      queryClient.invalidateQueries({ queryKey: ['validation', 'stability-evaluation', id] });
      navigate(`/validation/test-runs/${id}`);
    },
  });

  const testRun = blindData?.testRun;
  const testCase = blindData?.testCase;
  const executions = blindData?.executions || [];

  // Calculate consistency metrics
  const metrics = useMemo(() => {
    if (executions.length === 0) return null;
    return calculatePhase1Metrics(executions);
  }, [executions]);

  // Add key difference
  const addDifference = () => {
    if (newDifference.trim()) {
      setKeyDifferences((prev) => [...prev, newDifference.trim()]);
      setNewDifference('');
    }
  };

  const removeDifference = (index: number) => {
    setKeyDifferences((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle submit
  const handleSubmit = () => {
    if (!verdict || !metrics) return;

    submitMutation.mutate({
      verdict,
      verdictReasoning: verdictReasoning.trim() || undefined,
      observations: observations.trim() || undefined,
      keyDifferences,
      evaluatorConfidence: confidence,
      consistencyMetrics: metrics,
    });
  };

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (!testRun || executions.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <AlertCircle className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">No Executions Available</h2>
        <p className="mt-1 text-sm text-gray-500">
          Complete Phase 1 execution before starting stability evaluation.
        </p>
        <Link
          to={`/validation/test-runs/${id}`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Test Run
        </Link>
      </div>
    );
  }

  // Check if we're in read-only mode (revealed but has evaluation to view)
  const hasExistingEvaluation = !!existingEvalData?.currentUser?.phase1Evaluation;
  const isReadOnly = testRun.isRevealed;

  // If revealed but no evaluation exists, show message
  if (testRun.isRevealed && !hasExistingEvaluation) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-8 text-center">
        <Eye className="mx-auto h-10 w-10 text-amber-500" />
        <h2 className="mt-4 text-lg font-semibold text-gray-900">Results Already Revealed</h2>
        <p className="mt-1 text-sm text-gray-500">
          This test run has been revealed. No evaluation was submitted for Phase 1.
        </p>
        <Link
          to={`/validation/test-runs/${id}/stability-results`}
          className="mt-4 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700"
        >
          <Activity className="h-4 w-4" />
          View Results
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(`/validation/test-runs/${id}`)}
            className="rounded-lg p-2 hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Stability Evaluation - Phase 1</h1>
            <p className="text-sm text-gray-500">
              {testRun.name} - Evaluate consistency across {executions.length} iterations
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isReadOnly ? (
            <>
              <Eye className="h-5 w-5 text-green-600" />
              <span className="text-sm font-medium text-green-600">Revealed (Read Only)</span>
            </>
          ) : (
            <>
              <EyeOff className="h-5 w-5 text-amber-600" />
              <span className="text-sm font-medium text-amber-600">Blind Mode</span>
            </>
          )}
        </div>
      </div>

      {/* Test Case Context */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-3">
          <FileText className="h-5 w-5 text-gray-400" />
          <h2 className="text-lg font-semibold text-gray-900">Test Case Context</h2>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <span className="text-xs text-gray-500">Test Case</span>
            <p className="font-medium text-gray-900">{testCase?.name}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Condition</span>
            <p className="text-sm text-gray-900">{testCase?.condition}</p>
          </div>
          <div>
            <span className="text-xs text-gray-500">Patient</span>
            <p className="text-sm text-gray-900">
              {testCase?.patientInfo?.age} y/o {testCase?.patientInfo?.gender}
            </p>
          </div>
        </div>
      </div>

      {/* LLM Consistency Analysis (AI-powered) - Primary */}
      <LLMConsistencyAnalysis testRunId={id!} />

      {/* Side-by-Side Comparison */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Activity className="h-5 w-5 text-gray-600" />
          <h2 className="text-lg font-semibold text-gray-900">Side-by-Side Comparison</h2>
        </div>
        <IterationComparisonCard executions={executions} highlightDifferences={true} />
      </div>

      {/* Stability Verdict Selection */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <Star className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-gray-900">Stability Verdict</h2>
        </div>

        <div className="grid gap-3 sm:grid-cols-5">
          {STABILITY_VERDICTS.map((v) => {
            const Icon = v.icon;
            return (
              <button
                key={v.value}
                onClick={() => !isReadOnly && setVerdict(v.value)}
                disabled={isReadOnly}
                className={clsx(
                  'flex flex-col items-center gap-2 rounded-lg border-2 p-4 text-center transition-all',
                  verdict === v.value
                    ? v.color + ' ring-2 ring-offset-2'
                    : 'border-gray-200 hover:border-gray-300',
                  isReadOnly && 'cursor-not-allowed opacity-75'
                )}
              >
                <Icon className={clsx('h-6 w-6', verdict === v.value ? '' : 'text-gray-400')} />
                <span className="text-sm font-medium">{v.label}</span>
                <span className="text-xs opacity-75">{v.description}</span>
              </button>
            );
          })}
        </div>

        {/* Verdict Reasoning */}
        <div className="mt-4">
          <label className="text-sm font-medium text-gray-700">
            Reasoning (optional)
          </label>
          <textarea
            value={verdictReasoning}
            onChange={(e) => setVerdictReasoning(e.target.value)}
            rows={2}
            placeholder="Explain your stability assessment..."
            disabled={isReadOnly}
            className={clsx(
              'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
              isReadOnly && 'bg-gray-50 cursor-not-allowed'
            )}
          />
        </div>
      </div>

      {/* Key Differences */}
      <div className="rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <h2 className="text-lg font-semibold text-gray-900">Key Differences Observed</h2>
          <span className="text-xs text-gray-500">(optional)</span>
        </div>

        <div className="space-y-2">
          {keyDifferences.map((diff, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 rounded bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              <span className="flex-1">{diff}</span>
              {!isReadOnly && (
                <button
                  onClick={() => removeDifference(idx)}
                  className="text-amber-600 hover:text-amber-800"
                >
                  <XCircle className="h-4 w-4" />
                </button>
              )}
            </div>
          ))}
          {!isReadOnly && (
            <div className="flex gap-2">
              <input
                type="text"
                value={newDifference}
                onChange={(e) => setNewDifference(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addDifference()}
                placeholder="Describe a key difference you noticed..."
                className="flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none"
              />
              <button
                onClick={addDifference}
                className="rounded-lg bg-amber-100 px-4 py-2 text-amber-700 hover:bg-amber-200"
              >
                Add
              </button>
            </div>
          )}
          {isReadOnly && keyDifferences.length === 0 && (
            <p className="text-sm text-gray-500 italic">No key differences were recorded</p>
          )}
        </div>
      </div>

      {/* Additional Observations & Confidence */}
      <div className="rounded-xl border border-gray-200 bg-white p-6 space-y-4">
        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <MessageSquare className="h-4 w-4" />
            Additional Observations (optional)
          </label>
          <textarea
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
            rows={3}
            placeholder="Any other observations about the model's stability..."
            disabled={isReadOnly}
            className={clsx(
              'mt-1 w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500',
              isReadOnly && 'bg-gray-50 cursor-not-allowed'
            )}
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
            <Star className="h-4 w-4" />
            Confidence in Assessment: {confidence}/5
          </label>
          <input
            type="range"
            min="1"
            max="5"
            value={confidence}
            onChange={(e) => setConfidence(Number(e.target.value))}
            disabled={isReadOnly}
            className={clsx(
              'mt-2 w-full accent-amber-600',
              isReadOnly && 'cursor-not-allowed opacity-75'
            )}
          />
          <div className="flex justify-between text-xs text-gray-500">
            <span>Low confidence</span>
            <span>High confidence</span>
          </div>
        </div>
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white p-6">
        <div className="text-sm text-gray-600">
          {verdict ? (
            <span className="flex items-center gap-2 text-green-600">
              <Check className="h-4 w-4" />
              Verdict {isReadOnly ? 'recorded' : 'selected'}: {STABILITY_VERDICTS.find((v) => v.value === verdict)?.label}
            </span>
          ) : (
            <span className="text-amber-600">
              {isReadOnly ? 'No verdict was recorded' : 'Please select a stability verdict'}
            </span>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={handleSubmit}
            disabled={!verdict || submitMutation.isPending}
            className="inline-flex items-center gap-2 rounded-lg bg-amber-600 px-6 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            Submit Stability Evaluation
            <ArrowRight className="h-4 w-4" />
          </button>
        )}
        {isReadOnly && (
          <Link
            to={`/validation/test-runs/${id}/stability-results`}
            className="inline-flex items-center gap-2 rounded-lg bg-gray-100 px-6 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            <Activity className="h-4 w-4" />
            View Results
          </Link>
        )}
      </div>

      {/* Error Display */}
      {submitMutation.isError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="flex items-center gap-2 text-red-700">
            <AlertCircle className="h-5 w-5" />
            <span className="font-medium">Error</span>
          </div>
          <p className="mt-1 text-sm text-red-600">
            {submitMutation.error instanceof Error
              ? submitMutation.error.message
              : 'An unexpected error occurred'}
          </p>
        </div>
      )}
    </div>
  );
}
